import {
  Component, OnInit, OnDestroy, HostListener, ExistingProvider, forwardRef,
} from '@angular/core';
import {
  UntypedFormBuilder, UntypedFormGroup, Validators, ControlValueAccessor, NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { noop } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { IActivityWhat } from 'typings/section-types';

export const SECTION_ACTIVITY_WHAT_CONTROL_VALUE_ACCESSOR: ExistingProvider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => ActivityWhatSectionComponent),
  multi: true,
};
@Component({
    selector: 'app-activity-what-section',
    templateUrl: './activity-what-section.component.html',
    styleUrls: ['./activity-what-section.component.scss'],
    providers: [SECTION_ACTIVITY_WHAT_CONTROL_VALUE_ACCESSOR],
    standalone: false
})
export class ActivityWhatSectionComponent implements OnInit, OnDestroy, ControlValueAccessor {
  public form: UntypedFormGroup;
  private destroy$ = new Subject<void>();

  /** Value we will call whenever our form is touched */
  private onTouchedCallback: Function = noop;

  /** Function we will call whenever the value changes */
  private onChangeCallback: (val: IActivityWhat) => void = noop;

  @HostListener('blur') onBlur() {
    this.onTouchedCallback();
  }

  constructor(
    public fb: UntypedFormBuilder,
  ) {
    this.form = this.fb.group({
      title: ['', Validators.required],
      subtitle: [],
      content: ['', Validators.required],
    });
  }

  ngOnInit() {
    // Debounced value changes for better performance and reduced API calls
    this.form.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(300), // Debounce for 300ms to reduce excessive updates
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
    ).subscribe((formValue) => {
      this.onChangeCallback(formValue);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: IActivityWhat) {
    if (value) {
      this.form.get('title').setValue(value.title || '');
      this.form.get('subtitle').setValue(value.subtitle || '');
      this.form.get('content').setValue(value.content || '');
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
}
